import { FC, useCallback, useEffect, useState } from 'react'

import { useTheme } from '@mui/material'
import { CardTab, DataCardWithTabs } from '@/components/card'
import { DownloadCsv } from '@/components/DownloadCsv'
import { TableBase } from '@/components/table/base'
import { CANADA_COUNTRY, USA_COUNTRY } from '@/constants/regions'

import { StatisticsCsvData } from '../../StatisticsCard/StatisticsCard'
import { makeStatisticsFilename } from '../../utils'
import { ComponentWithStatisticsQueryParams } from '../types'
import { TabKey, useRegionTabs } from './tabs/useRegionTabs'

export const REGION_TITLE = 'Region' as const

export const RegionCard: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const theme = useTheme()
  const tabs = useRegionTabs(queryParams)

  const getTabFromQueryParams = useCallback(() => {
    const { countryCode, subdivision } = queryParams

    if (!subdivision?.length) {
      return tabs[0].value
    }

    if (countryCode === USA_COUNTRY.iso2.toUpperCase()) {
      return tabs[1].value
    }

    if (countryCode === CANADA_COUNTRY.iso2.toUpperCase()) {
      return tabs[2].value
    }

    return tabs[0].value
  }, [queryParams.countryCode, queryParams.subdivision, tabs])

  const [currentTab, setCurrentTab] = useState<TabKey>(getTabFromQueryParams())

  useEffect(() => {
    setCurrentTab(getTabFromQueryParams())
  }, [queryParams.countryCode, queryParams.subdivision, getTabFromQueryParams])

  const [csvData, setCsvData] = useState<StatisticsCsvData | null>(null)

  useEffect(() => {
    setCsvData(tabs.find((t) => t.value === currentTab)?.csvData)
  }, [currentTab])

  return (
    <DataCardWithTabs
      Sx={{
        card: {
          boxShadow: 'none'
        },
        header: {
          backgroundColor: theme.palette.statistics.panel.header.backgroundColor
        }
      }}
      title={REGION_TITLE}
      tabs={tabs}
      currentTab={currentTab}
      onTabChange={(tab) => {
        setCurrentTab(tab as TabKey)
      }}
      headerPostfixActions={
        csvData && (
          <DownloadCsv
            asIcon
            csvFileName={makeStatisticsFilename(currentTab)}
            formatCsvRow={(row) => Object.values(row).map((value) => String(value))}
            {...csvData}
          />
        )
      }
    >
      {tabs.map((tab) => {
        return (
          <CardTab key={tab.value} value={tab.value}>
            <TableBase
              dense
              defaultSort={tab.defaultSort}
              loading={!tab.data}
              columns={tab.columns}
              data={tab.data || []}
              clientSideSortAndPagination
            />
          </CardTab>
        )
      })}
    </DataCardWithTabs>
  )
}
